import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch } from '@app/hooks';
import { announcementPopupsCleared } from '@features/announcements/model/slice';
import { Modal } from '@shared/ui/Modal';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { IconChevronDown, IconX } from '@shared/ui/icons';
import { riseIn } from '@shared/theme/animations';
import {
  useMeQuery,
  useListAnnouncementsQuery,
  useMarkAllAnnouncementsReadMutation,
  useMarkAnnouncementSeenMutation,
  useClearAnnouncementMutation,
  useClearAllAnnouncementsMutation,
} from '@shared/api';
import type { UserContext } from '@entities/user/types';
import {
  announcementAgentLabel,
  formatPublishedAt,
  kindMeta,
  type Announcement,
  type AnnouncementKind,
} from '@entities/announcement';

/** Сентинел-ключ группы общесистемных обновлений (agent_code === null). */
const SYSTEM_KEY = '__system__';

interface Props {
  open: boolean;
  onClose: () => void;
}

/* ── layout ──────────────────────────────────────────────────────────────── */

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Lead = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ClearAllBtn = styled.button`
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(3)};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3.5)};
`;

const Group = styled.section<{ $highlighted?: boolean }>`
  border: 1px solid
    ${({ theme, $highlighted }) => ($highlighted ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
`;

const GroupHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.text.muted};
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};
`;

const NewPill = styled.span`
  padding: 2px ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: 700;
`;

const HintText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const GroupRight = styled.div`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const GroupCount = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacing(1.5)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const GroupBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding-top: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

/* ── card ────────────────────────────────────────────────────────────────── */

const CardRow = styled.article<{ $tone: AnnouncementKind; $unread: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 3px 1fr auto;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3.5)} ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $unread }) => ($unread ? theme.colors.primarySoft : 'transparent')};
  animation: ${riseIn} ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut} both;

  &::before {
    content: '';
    grid-column: 1;
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme, $tone }) => kindMeta($tone).color(theme)};
  }
`;

const CardMain = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const KindBadge = styled(Badge)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardBody = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const CardTime = styled.time`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const RemoveBtn = styled.button`
  grid-column: 3;
  align-self: start;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  transition:
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing(10)} ${({ theme }) => theme.spacing(4)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

/* ── grouping model ──────────────────────────────────────────────────────── */

interface GroupModel {
  key: string;
  agentCode: string | null;
  label: string;
  items: Announcement[];
  newCount: number;
  isMyGroup: boolean;
  rank: number;
  defaultExpanded: boolean;
}

/**
 * Определить профильные («свои») разделы агентов для текущего пользователя.
 *
 * Позволяет открывать развёрнутой именно ту вкладку, с которой человек реально
 * работает (например, для геодезиста — «Геодезист», для инженера ПТО — «Инженер (ПТО)»),
 * а вкладки остальных агентов показывать свёрнутыми, чтобы человек видел рост
 * всей системы и при желании мог развернуть любую вкладку кликом либо просто
 * закрыть модалку крестиком.
 */
function resolveUserPrimaryAgents(me: UserContext | undefined, pathname: string): Set<string> {
  const result = new Set<string>();

  // 1. Если пользователь прямо сейчас находится в разделе конкретного агента (/agents/<code>).
  const routeMatch = /^\/agents\/([a-z0-9_-]+)/i.exec(pathname);
  if (routeMatch?.[1]) {
    result.add(routeMatch[1].toLowerCase());
  }

  // 2. Отдел в профиле пользователя.
  const dept = (me?.user?.department ?? '').toLowerCase();
  if (dept.includes('геодез')) result.add('geodesy');
  if (dept.includes('пто')) result.add('pto');
  if (dept.includes('сцо') || dept.includes('смет')) result.add('smeta');
  if (dept.includes('юрид') || dept.includes('право') || dept.includes('договор')) {
    result.add('statut');
    result.add('dispatcher');
  }
  if (dept.includes('мто') || dept.includes('снабж') || dept.includes('закуп')) {
    result.add('mto');
  }
  if (dept.includes('эконом')) result.add('econ');
  if (dept.includes('финанс') || dept.includes('бухгалт') || dept.includes('казнач')) {
    result.add('finance');
    result.add('econ');
  }
  if (dept.includes('кадр') || dept.includes('персонал')) result.add('kadry');
  if (dept.includes('ксп') || dept.includes('планир')) result.add('ksp');
  if (dept.includes('логист') || dept.includes('трансп')) result.add('logist');
  if (dept.includes('генеральный директор') || dept.includes('руководств')) {
    result.add('ceo');
    result.add('progress');
  }

  // 3. Профильные роли пользователя.
  const roles = me?.roles ?? [];
  const isBroadManager =
    roles.includes('manager') || roles.includes('director') || roles.includes('admin');

  for (const role of roles) {
    switch (role) {
      case 'surveyor':
        result.add('geodesy');
        break;
      case 'engineer':
      case 'engineer_plus':
      case 'pto_engineer':
        result.add('pto');
        result.add('field');
        break;
      case 'foreman':
      case 'field_specialist_plus':
        result.add('field');
        break;
      case 'estimator':
      case 'estimator_plus':
      case 'chief_estimator':
        result.add('smeta');
        break;
      case 'economist':
      case 'economist_plus':
      case 'chief_economist':
        result.add('econ');
        break;
      case 'financier':
        result.add('finance');
        result.add('econ');
        break;
      case 'lawyer':
        result.add('statut');
        result.add('dispatcher');
        break;
      case 'mto_specialist':
      case 'mto_specialist_plus':
      case 'supplier':
        result.add('mto');
        break;
      case 'logist':
        result.add('logist');
        break;
      case 'ksp_engineer':
        result.add('ksp');
        break;
      case 'meeting_organizer':
        result.add('meetings');
        break;
      case 'pm':
      case 'pm_specialist':
        result.add('progress');
        break;
      case 'hr_admin':
      case 'hr_auditor':
        result.add('kadry');
        break;
      case 'hr':
        if (!isBroadManager || dept.includes('кадр')) {
          result.add('kadry');
        }
        break;
      case 'dispatcher_operator':
        if (!isBroadManager || dept.includes('юрид') || dept.includes('диспетч')) {
          result.add('dispatcher');
        }
        break;
      default:
        break;
    }
  }

  // 4. Фолбэк по точечным правам записи (если у пользователя узкий набор агентов).
  if (result.size === 0) {
    const writable = (me?.agents ?? [])
      .filter((a) => a.can_write)
      .map((a) => a.agent_code);
    const withoutAuxiliaryKadry =
      writable.length > 1 ? writable.filter((code) => code !== 'kadry') : writable;
    if (withoutAuxiliaryKadry.length > 0 && withoutAuxiliaryKadry.length <= 4) {
      for (const code of withoutAuxiliaryKadry) {
        result.add(code);
      }
    }
  }

  return result;
}

function AnnouncementCard({
  item,
  onClear,
}: {
  item: Announcement;
  onClear: (id: string) => void;
}) {
  const meta = kindMeta(item.kind);
  const { Icon } = meta;
  return (
    <CardRow $tone={item.kind} $unread={item.popup_seen_at == null || item.read_at == null}>
      <CardMain>
        <CardHead>
          <KindBadge $tone={meta.tone}>
            <Icon size={12} />
            {meta.label}
          </KindBadge>
          <CardTitle>{item.title}</CardTitle>
        </CardHead>
        {item.body && <CardBody>{item.body}</CardBody>}
        <CardTime dateTime={item.published_at ?? undefined}>
          {formatPublishedAt(item.published_at)}
        </CardTime>
      </CardMain>
      <RemoveBtn type="button" onClick={() => onClear(item.id)} aria-label="Убрать обновление">
        <IconX size={16} />
      </RemoveBtn>
    </CardRow>
  );
}

export function UpdatesModal({ open, onClose }: Props) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { data: me } = useMeQuery();
  const { data: announcements = [] } = useListAnnouncementsQuery();
  const [markAllRead] = useMarkAllAnnouncementsReadMutation();
  const [markSeen] = useMarkAnnouncementSeenMutation();
  const [clearOne] = useClearAnnouncementMutation();
  const [clearAll] = useClearAllAnnouncementsMutation();

  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const seenSent = useRef<Set<string>>(new Set());

  // При открытии гасим бейдж колокольчика: видимые непрочитанные помечаем прочитанными.
  useEffect(() => {
    if (!open) return;
    if (announcements.some((a) => a.read_at == null)) {
      void markAllRead();
    }
  }, [open, announcements, markAllRead]);

  // При закрытии модалки (крестиком, кнопкой «Понятно», Esc или кликом по фону)
  // помечаем все непосмотренные обновления как `seen`, чтобы после перезагрузки
  // страницы модалка не открывалась повторно до выхода следующего обновления.
  const handleClose = useCallback(() => {
    for (const a of announcements) {
      if (a.popup_seen_at == null && !seenSent.current.has(a.id)) {
        seenSent.current.add(a.id);
        void markSeen(a.id);
      }
    }
    dispatch(announcementPopupsCleared());
    setOverrides({});
    onClose();
  }, [announcements, markSeen, dispatch, onClose]);

  const myAgentCodes = useMemo(
    () => resolveUserPrimaryAgents(me, location.pathname),
    [me, location.pathname],
  );

  const hasUnseenInFeed = useMemo(
    () => announcements.some((a) => a.popup_seen_at == null),
    [announcements],
  );

  const groups = useMemo<GroupModel[]>(() => {
    const byCode = new Map<string, Announcement[]>();
    for (const a of announcements) {
      const key = a.agent_code ?? SYSTEM_KEY;
      const bucket = byCode.get(key);
      if (bucket) bucket.push(a);
      else byCode.set(key, [a]);
    }

    const result: GroupModel[] = [];
    for (const [key, items] of byCode) {
      const agentCode = key === SYSTEM_KEY ? null : key;
      items.sort((a, b) => (b.published_at ?? '').localeCompare(a.published_at ?? ''));
      const newCount = items.filter((a) => a.popup_seen_at == null).length;
      const isMyGroup = agentCode === null || myAgentCodes.has(agentCode);

      // Если модалка открылась при выходе новых обновлений (hasUnseenInFeed),
      // автоматически раскрываем ТОЛЬКО вкладки своего агента (или «Система»),
      // в которых есть новые обновления. Вкладки других агентов остаются
      // свёрнутыми — работник видит, что вышло обновление, и может развернуть
      // вкладку кликом или закрыть модалку крестиком.
      const defaultExpanded = hasUnseenInFeed ? isMyGroup && newCount > 0 : isMyGroup;

      const rank =
        isMyGroup && newCount > 0
          ? 0
          : newCount > 0
            ? 1
            : isMyGroup
              ? 2
              : 3;

      result.push({
        key,
        agentCode,
        label: announcementAgentLabel(agentCode),
        items,
        newCount,
        isMyGroup,
        rank,
        defaultExpanded,
      });
    }
    result.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
    return result;
  }, [announcements, myAgentCodes, hasUnseenInFeed]);

  const isExpanded = (g: GroupModel): boolean => overrides[g.key] ?? g.defaultExpanded;
  const toggle = (g: GroupModel) =>
    setOverrides((o) => ({ ...o, [g.key]: !(o[g.key] ?? g.defaultExpanded) }));

  return (
    <Modal open={open} title="Что нового в ОСНОВЕ" onClose={handleClose}>
      {announcements.length === 0 ? (
        <EmptyState>Пока нет обновлений. Здесь появятся новости о работе агентов.</EmptyState>
      ) : (
        <>
          <Toolbar>
            <Lead>
              Обновления по разделам системы. Нажмите на вкладку агента, чтобы развернуть список
              задач.
            </Lead>
            <ClearAllBtn type="button" onClick={() => void clearAll()}>
              Очистить всё
            </ClearAllBtn>
          </Toolbar>
          <Groups>
            {groups.map((g) => {
              const expanded = isExpanded(g);
              return (
                <Group key={g.key} $highlighted={g.newCount > 0}>
                  <GroupHeader type="button" aria-expanded={expanded} onClick={() => toggle(g)}>
                    <Chevron $open={expanded}>
                      <IconChevronDown size={18} />
                    </Chevron>
                    <span>{g.label}</span>
                    {g.newCount > 0 && <NewPill>Новое: {g.newCount}</NewPill>}
                    <GroupRight>
                      {!expanded && <HintText>Развернуть</HintText>}
                      <GroupCount>{g.items.length}</GroupCount>
                    </GroupRight>
                  </GroupHeader>
                  {expanded && (
                    <GroupBody>
                      {g.items.map((item) => (
                        <AnnouncementCard
                          key={item.id}
                          item={item}
                          onClear={(id) => void clearOne(id)}
                        />
                      ))}
                    </GroupBody>
                  )}
                </Group>
              );
            })}
          </Groups>
          <Footer>
            <Button type="button" $variant="primary" $size="sm" onClick={handleClose}>
              Понятно
            </Button>
          </Footer>
        </>
      )}
    </Modal>
  );
}
